import React, { useEffect, useMemo, useState } from 'react';
import { INK } from '../../Utils/styleTokens';
import { uiLabel } from '../../Utils/Labeling';
import {
  getAdvancedConfig,
  saveAdvancedConfig,
  getEntityAlgorithmMap,
  getMaskingAlgorithms,
} from '../api';

// Entity → C2 Algorithm Mapping screen. Overrides the shipped default entity→algorithm map
// install-wide. The effective (merged) baseline is what discovery recommendations start from;
// each source can still override per-field in the Masking Inventory.
const AlgorithmMappingScreen = ({ canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [algorithms, setAlgorithms] = useState([]); // [{ id, label }]
  const [effectiveMap, setEffectiveMap] = useState({}); // merged (default + override), current values
  const [override, setOverride] = useState({}); // saved install-wide override (subset)
  // Healthcare (HIPAA Safe Harbor) profile: shipped overlay (read-only defaults) + operator
  // overrides stored SEPARATELY from the baseline overrides. Applied only to healthcare-tagged
  // sources, where it wins over the baseline.
  const [safeHarborDefaults, setSafeHarborDefaults] = useState({});
  const [shOverride, setShOverride] = useState({});
  const [shAddCode, setShAddCode] = useState('');
  const [entityLevels, setEntityLevels] = useState({}); // preserved on save (shared blob)
  const [maxRowsPerTable, setMaxRowsPerTable] = useState(null); // preserved on save (shared blob)
  // Preserved on save (full-replace PUT). Tri-state: null = unset -> worker default.
  const [scanEmbeddedStructures, setScanEmbeddedStructures] = useState(null);
  const [entityOrder, setEntityOrder] = useState([]); // preserved on save (shared blob)
  const [profileEnabled, setProfileEnabled] = useState(true); // preserved on save (shared blob)
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [map, algos, cfg] = await Promise.all([
          getEntityAlgorithmMap(),
          getMaskingAlgorithms(),
          getAdvancedConfig(),
        ]);
        if (!alive) return;
        setEffectiveMap(map || {});
        setAlgorithms(normalizeAlgos(algos));
        setOverride(cfg.entityTypeToAlgorithm || {});
        setSafeHarborDefaults(cfg.safeHarborDefaults || {});
        setShOverride(cfg.entityTypeToAlgorithmSafeHarbor || {});
        setEntityLevels(cfg.entityLevels || {});
        setMaxRowsPerTable(cfg.maxRowsPerTable != null ? cfg.maxRowsPerTable : null);
        setScanEmbeddedStructures(
          cfg.scanEmbeddedStructures != null ? cfg.scanEmbeddedStructures : null);
        setEntityOrder(Array.isArray(cfg.entityOrder) ? cfg.entityOrder : []);
        setProfileEnabled(cfg.profileEnabled !== false);
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load algorithm map');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Rows: union of entities present in the effective map (so operators see the full baseline).
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return Object.keys(effectiveMap)
      .map((code) => ({ code, label: uiLabel(code) }))
      .filter((r) => !q || r.code.toLowerCase().includes(q) || r.label.toLowerCase().includes(q))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [effectiveMap, filter]);

  const valueOf = (code) => effectiveMap[code] || '';
  const isOverridden = (code) => Object.prototype.hasOwnProperty.call(override, code);

  const setValue = (code, algoId) => {
    setMessage(null);
    setEffectiveMap((prev) => ({ ...prev, [code]: algoId }));
    setOverride((prev) => ({ ...prev, [code]: algoId }));
  };

  const resetRow = (code) => {
    setMessage(null);
    setOverride((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
    // Effective value can't be recomputed client-side without the shipped default; leave the
    // displayed value as-is but drop it from the override. A reload shows the true default.
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await saveAdvancedConfig({
        entityLevels,
        entityTypeToAlgorithm: override,
        entityTypeToAlgorithmSafeHarbor: shOverride,
        maxRowsPerTable,
        scanEmbeddedStructures,
        profileEnabled,
        entityOrder,
      });
      setOverride(res.entityTypeToAlgorithm || {});
      setShOverride(res.entityTypeToAlgorithmSafeHarbor || {});
      // Re-fetch the merged map so reset rows show their true shipped default.
      const map = await getEntityAlgorithmMap();
      setEffectiveMap(map || {});
      setMessage('Saved. New masking recommendations use this baseline for all users.');
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const overrideCount = Object.keys(override).length;

  // Healthcare profile rows: union of the shipped overlay and operator additions.
  const shRows = useMemo(() => {
    const codes = new Set([...Object.keys(safeHarborDefaults), ...Object.keys(shOverride)]);
    return [...codes]
      .map((code) => ({ code, label: uiLabel(code) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [safeHarborDefaults, shOverride]);

  const shValueOf = (code) =>
    Object.prototype.hasOwnProperty.call(shOverride, code)
      ? shOverride[code]
      : safeHarborDefaults[code] || '';
  const shIsOverridden = (code) => Object.prototype.hasOwnProperty.call(shOverride, code);
  const shIsShipped = (code) => Object.prototype.hasOwnProperty.call(safeHarborDefaults, code);

  const shSetValue = (code, algoId) => {
    setMessage(null);
    setShOverride((prev) => ({ ...prev, [code]: algoId }));
  };

  // Reset drops the operator override; a shipped row falls back to the overlay default, an
  // operator-added row disappears from the profile entirely.
  const shResetRow = (code) => {
    setMessage(null);
    setShOverride((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  // Entities that can still be added to the healthcare profile (any entity in the effective
  // baseline map that isn't already a profile row).
  const shAddable = useMemo(() => {
    const existing = new Set([...Object.keys(safeHarborDefaults), ...Object.keys(shOverride)]);
    return Object.keys(effectiveMap)
      .filter((code) => !existing.has(code))
      .map((code) => ({ code, label: uiLabel(code) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [effectiveMap, safeHarborDefaults, shOverride]);

  const shAdd = () => {
    if (!shAddCode) return;
    setMessage(null);
    // Seed the new row from the baseline's effective value so adding is never a silent change.
    setShOverride((prev) => ({ ...prev, [shAddCode]: effectiveMap[shAddCode] || '' }));
    setShAddCode('');
  };

  return (
    <div>
      <h1 className='adv-config-screen-title'>Entity → Algorithm Mapping</h1>
      <p className='adv-config-screen-desc'>
        Override the platform's default masking algorithm for each entity type, install-wide. This
        becomes the new baseline everywhere masking recommendations are shown. Individual data
        sources can still override per-field in the Masking Inventory.
      </p>

      <div className='adv-config-card'>
        <div className='adv-config-toolbar'>
          <input
            data-testid='filter-adv-config-algorithms'
            className='adv-config-search'
            placeholder='Filter entities…'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className='adv-config-table'>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Entity</th>
                <th style={{ width: '40%' }}>Algorithm</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ code, label }) => (
                <tr key={code}>
                  <td>
                    {label}
                    <span style={{ color: INK[400], marginLeft: 8, fontSize: 11 }}>{code}</span>
                    {isOverridden(code) && <span className='adv-config-badge-modified'>Overridden</span>}
                  </td>
                  <td>
                    <select className='adv-config-select' style={{ minWidth: 260 }}
                      value={valueOf(code)} disabled={!canEdit || saving}
                      onChange={(e) => setValue(code, e.target.value)}>
                      {/* Ensure the current value is selectable even if not in the algorithms list. */}
                      {valueOf(code) && !algorithms.some((a) => a.id === valueOf(code)) && (
                        <option value={valueOf(code)}>{valueOf(code)}</option>
                      )}
                      {algorithms.map((a) => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {isOverridden(code) && canEdit && (
                      <button type='button' className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => resetRow(code)} disabled={saving}>
                        Reset to default
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} style={{ color: INK[500] }}>No entities in the map match “{filter}”.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <h1 className='adv-config-screen-title' style={{ marginTop: 24 }}>Healthcare (HIPAA Safe Harbor) Profile</h1>
      <p className='adv-config-screen-desc'>
        Alternate defaults applied instead of the baseline for data sources auto-tagged as
        healthcare. Edits here are healthcare-scoped compliance decisions and win over both the
        shipped Safe Harbor overlay and any baseline override above. Entities not listed fall back
        to the baseline mapping.
      </p>

      <div className='adv-config-card'>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className='adv-config-table' data-testid='safe-harbor-table'>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Entity</th>
                <th style={{ width: '40%' }}>Healthcare Algorithm</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shRows.map(({ code, label }) => (
                <tr key={code}>
                  <td>
                    {label}
                    <span style={{ color: INK[400], marginLeft: 8, fontSize: 11 }}>{code}</span>
                    {shIsOverridden(code) && <span className='adv-config-badge-modified'>Overridden</span>}
                  </td>
                  <td>
                    <select className='adv-config-select' style={{ minWidth: 260 }}
                      value={shValueOf(code)} disabled={!canEdit || saving}
                      onChange={(e) => shSetValue(code, e.target.value)}>
                      {shValueOf(code) && !algorithms.some((a) => a.id === shValueOf(code)) && (
                        <option value={shValueOf(code)}>{shValueOf(code)}</option>
                      )}
                      {algorithms.map((a) => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {shIsOverridden(code) && canEdit && (
                      <button type='button' className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => shResetRow(code)} disabled={saving}>
                        {shIsShipped(code) ? 'Reset to Safe Harbor default' : 'Remove from profile'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {shRows.length === 0 && (
                <tr><td colSpan={3} style={{ color: INK[500] }}>No healthcare profile entries.</td></tr>
              )}
            </tbody>
          </table>
        )}
        {canEdit && !loading && (
          <div className='adv-config-toolbar'>
            <select className='adv-config-select' data-testid='safe-harbor-add-select'
              value={shAddCode} disabled={saving} onChange={(e) => setShAddCode(e.target.value)}>
              <option value=''>Add entity to healthcare profile…</option>
              {shAddable.map((r) => (
                <option key={r.code} value={r.code}>{r.label} ({r.code})</option>
              ))}
            </select>
            <button type='button' className='adv-config-btn-secondary adv-config-btn'
              data-testid='safe-harbor-add-btn'
              onClick={shAdd} disabled={saving || !shAddCode}>
              Add
            </button>
          </div>
        )}
      </div>

      <div className='adv-config-toolbar'>
        <button type='button' className='adv-config-btn' onClick={save} disabled={!canEdit || saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <span style={{ fontSize: 12, color: INK[500] }}>
          {overrideCount === 0 ? 'No overrides — using shipped defaults' : `${overrideCount} override${overrideCount === 1 ? '' : 's'}`}
        </span>
        {message && <span className='adv-config-msg-ok'>{message}</span>}
        {error && <span className='adv-config-msg-err'>{error}</span>}
      </div>
    </div>
  );
};

function normalizeAlgos(list) {
  return (Array.isArray(list) ? list : [])
    .map((a) => {
      if (typeof a === 'string') return { id: a, label: a };
      const id = a?.id ?? a?.value ?? a?.name;
      if (id == null) return null;
      return { id, label: a?.label || a?.name || String(id) };
    })
    .filter(Boolean);
}

export default AlgorithmMappingScreen;
